import { useMutation } from '@apollo/client';

import { cacheUtils } from '@eluve/apollo-client';
import { AmendmentHeader, FCC, VStack, useToast } from '@eluve/components';
import {
  appointmentAmendments,
  useAmendmentActivity,
  useAppointmentId,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserName } from '@eluve/smart-blocks';

const deleteBillingCodeAmendmentMutation = graphql(`
  mutation deleteBillingCodeAmendment($tenantId: uuid!, $id: uuid!) {
    deleteBillingCodeAmendmentsByPk(tenantId: $tenantId, id: $id) {
      __typename
      id
    }
  }
`);

export const AppointmentBillingCodesEditorContainer: FCC<{
  refetchBillingCodes: () => void;
}> = ({ children, refetchBillingCodes }) => {
  const toast = useToast();
  const appointmentId = useAppointmentId();
  const analytics = useAnalytics('amendments');
  const { isBillingCodeAmendmentInProgress, activeAmendment } =
    useAmendmentActivity();

  const activeAmendmentUserName = useUserName(activeAmendment?.userId ?? '');

  const [deleteBillingCodeAmendment] = useMutation(
    deleteBillingCodeAmendmentMutation,
    {
      optimisticResponse: () => ({
        deleteBillingCodeAmendmentsByPk: {
          __typename: 'BillingCodeAmendments' as const,
          id: activeAmendment!.billing_code_amendment!.id,
        },
      }),
      onError: () => toast.error('Failed to delete billing code amendment'),
      update: () => {
        if (!activeAmendment) {
          return;
        }

        cacheUtils.updateFragment(
          {
            fragment: appointmentAmendments,
            key: { id: appointmentId },
          },
          (existing) => {
            if (!existing) {
              return null;
            }

            return {
              ...existing,
              amendments: existing.amendments.map((amendment) =>
                amendment.id === activeAmendment!.id
                  ? {
                      ...amendment,
                      billing_code_amendment: null,
                    }
                  : amendment,
              ),
            };
          },
        );

        cacheUtils.evict({
          key: { id: activeAmendment!.billing_code_amendment!.id },
          typeName: 'BillingCodeAmendments',
        });
      },
      onCompleted: () => {
        analytics.capture('user_discarded_billing_code_amendment', {
          tenantId: activeAmendment!.tenantId,
          appointmentId,
          userId: activeAmendment!.userId,
          amendmentId: activeAmendment!.id,
          billingCodeAmendmentId: activeAmendment!.billing_code_amendment!.id,
        });
      },
    },
  );

  const onDeleteBillingCodeAmendment = async () => {
    await deleteBillingCodeAmendment({
      variables: {
        tenantId: activeAmendment!.tenantId,
        id: activeAmendment!.billing_code_amendment!.id,
      },
    });

    refetchBillingCodes();
  };

  if (!isBillingCodeAmendmentInProgress) {
    return children;
  }

  return (
    <VStack
      gap={6}
      className="rounded-xl border border-border-primary bg-white p-6"
    >
      <AmendmentHeader
        date={activeAmendment!.billing_code_amendment!.createdAt}
        userName={activeAmendmentUserName}
        isReadonly={false}
        onDelete={onDeleteBillingCodeAmendment}
      />

      {children}
    </VStack>
  );
};
